import { create } from 'zustand';
import type { EnvName, MetricWindow, Overview } from '../../../shared/types';
import { api } from '../api/client';

interface EnvState {
  env: EnvName;
  window: MetricWindow;
  overview: Partial<Record<EnvName, Overview>>;
  loading: boolean;
  error: string | null;
  setEnv: (env: EnvName) => void;
  setWindow: (w: MetricWindow) => void;
  load: () => Promise<void>;
}

function remembered<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try { const v = localStorage.getItem(key); if (v && (allowed as readonly string[]).includes(v)) return v as T; } catch { /* storage unavailable */ }
  return fallback;
}

export const useEnvStore = create<EnvState>((set, get) => ({
  env: remembered('pd-env', ['prod', 'test'] as const, 'prod'),
  window: remembered('pd-window', ['1h', '24h', '7d'] as const, '1h'),
  overview: {},
  loading: false,
  error: null,
  setEnv: (env) => { try { localStorage.setItem('pd-env', env); } catch { /* ignore */ } set({ env }); void get().load(); },
  setWindow: (w) => { try { localStorage.setItem('pd-window', w); } catch { /* ignore */ } set({ window: w }); void get().load(); },
  load: async () => {
    const { env, window } = get();
    set({ loading: true, error: null });
    try {
      const data = await api<Overview>(`/api/env/${env}/overview?window=${window}`);
      // discard if the user switched environment mid-flight
      if (get().env !== env) return;
      set((s) => ({ overview: { ...s.overview, [env]: data }, loading: false }));
    } catch (err) {
      if (get().env !== env) return;
      set({ loading: false, error: (err as Error).message });
    }
  },
}));
